import classNames from 'classnames';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { ReactComponent as EventLinkIcon } from 'src/assets/event-arrow.svg';
import css from './carousel.module.scss';
import { useResize } from 'src/hooks/shared-hooks';

export interface CarusselItem {
  id: string;
  imgUrl: string;
  title: string;
  description: string;
  itemsQuantity?: number;
  slug: string;
}

interface CarusselProps {
  data: ReactElement[];
  title: ReactElement;
  itemsToShow?: number;
}

export const Carousel = ({ data, title, itemsToShow = 3 }: CarusselProps) => {
  const [index, setIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  const dimensions = useResize();
  const desktopMode = dimensions.width > 1000;
  const mobileMode = dimensions.width < 500;

  const showItems = useMemo(() => {
    if (mobileMode || itemsToShow === 1) return 1;
    if (itemsToShow >= 2 && desktopMode) {
      return itemsToShow;
    } else {
      return 2;
    }
  }, [desktopMode, itemsToShow, mobileMode]);

  const showSlider = data.length > showItems;

  const btnsCount = showSlider ? data.length - showItems + 1 : 0;

  const showNextImage = () => index < btnsCount - 1 && setIndex((index) => (index === btnsCount - 1 ? 0 : index + 1));

  const showPrevImage = () => index !== 0 && setIndex((index) => (index === 0 ? btnsCount - 1 : index - 1));

  const handleTouchStart = (e: React.TouchEvent) => setTouchStart(e.targetTouches[0].clientX);

  const handleTouchMove = (e: React.TouchEvent) => setTouchEnd(e.targetTouches[0].clientX);

  const handleTouchEnd = () => {
    if (touchStart - touchEnd > 150 && showSlider) {
      showNextImage();
    }

    if (touchStart - touchEnd < -150 && showSlider) {
      showPrevImage();
    }
  };

  useEffect(() => {
    if (index >= btnsCount && btnsCount > 0) {
      setIndex(btnsCount - 1);
    }
  }, [btnsCount, desktopMode, index]);

  useEffect(() => {
    setIndex(0);
  }, [data]);

  return (
    <div className={css.carousel}>
      <header>
        {title}
        {showSlider && (
          <div className={css.arrows}>
            <EventLinkIcon
              onClick={showPrevImage}
              className={classNames(css.arrow, css.arrow_left, { [css.disabled]: index === 0 })}
            />
            <EventLinkIcon
              onClick={showNextImage}
              className={classNames(css.arrow, css.arrow_right, { [css.disabled]: index === btnsCount - 1 })}
            />
          </div>
        )}
      </header>
      <div className={css.carousel_body}>
        <div
          className={css.items}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}>
          {data.map((item: ReactElement, i) => (
            <div
              key={`${item.key}-${i}`}
              aria-hidden={index !== i}
              className={css.item}
              style={{
                translate: `calc(${-100 * index}% -  ${index}rem)`,
                opacity: i > index + itemsToShow - 1 ? 0 : 1
              }}>
              {item}
            </div>
          ))}
        </div>
        <div className={css.indicators}>
          {showSlider &&
            [...Array(btnsCount).keys()].map((_, i: number) => (
              <button
                key={i} /* NOSONAR */
                className={classNames(css.indicator, { [css.indicator_inactive]: i === index })}
                onClick={() => setIndex(i)}></button>
            ))}
        </div>
      </div>
    </div>
  );
};
